import React from 'react';
import { useCarePlans } from '../services/api';
import useMockDataToast from '../hooks/useMockDataToast';

const CarePlans: React.FC = () => {
  const { data, isLoading, isFallback } = useCarePlans();
  useMockDataToast(isFallback, 'Care coordination service unreachable. Showing standby plan.');
  const { rounds, reminders, surgeryBoard, inventoryAlerts } = data;

  return (
    <div className="space-y-8">
      <div className="flex flex-col gap-2">
        <p className="section-title">Interdisciplinary care</p>
        <h1 className="text-2xl font-semibold text-slate-900">Care plans</h1>
        <p className="text-sm text-slate-500">
          Coordinate rounds, follow-ups, and surgical prep with real-time signals from each team.
        </p>
      </div>

      <section className="glass-panel overflow-hidden">
        <div className="border-b border-slate-100 px-6 py-4">
          <h2 className="text-lg font-semibold text-slate-900">Medical rounds timeline</h2>
          <p className="text-sm text-slate-500">Clinical priorities requiring director approval.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] table-auto divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-6 py-3">Patient</th>
                <th className="px-6 py-3">Diagnosis</th>
                <th className="px-6 py-3">Attending</th>
                <th className="px-6 py-3">Priority</th>
                <th className="px-6 py-3">Next step</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white text-slate-700">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-10 text-center text-slate-400">
                    Loading care timeline...
                  </td>
                </tr>
              ) : (
                rounds.map((round) => (
                  <tr key={round.patient} className="hover:bg-slate-50">
                    <td className="px-6 py-4 font-semibold text-slate-900">{round.patient}</td>
                    <td className="px-6 py-4">{round.diagnosis}</td>
                    <td className="px-6 py-4">{round.attending}</td>
                    <td className="px-6 py-4">
                      <span
                        className={`pill ${
                          round.priority === 'Critical'
                            ? 'bg-rose-50 text-rose-600'
                            : round.priority === 'High'
                              ? 'bg-amber-50 text-amber-600'
                              : 'bg-emerald-50 text-emerald-600'
                        }`}
                      >
                        {round.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-slate-500">{round.nextStep}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <div className="glass-panel space-y-4 p-6 lg:col-span-2">
          <div className="flex items-center justify-between">
            <div>
              <p className="section-title mb-1">Automation queue</p>
              <h3 className="text-lg font-semibold text-slate-900">Follow-up reminders</h3>
            </div>
            <button className="rounded-xl border border-slate-200 px-4 py-2 text-xs font-semibold text-slate-600 hover:border-brand-blue hover:text-brand-blue">
              Configure channels
            </button>
          </div>
          {isLoading ? (
            <p className="rounded-2xl border border-dashed border-slate-200 p-4 text-sm text-slate-400">
              Preparing automation queue...
            </p>
          ) : (
            <ul className="space-y-3">
              {reminders.map((reminder) => (
                <li
                  key={reminder.title}
                  className="flex items-center justify-between rounded-2xl border border-slate-100 bg-white px-4 py-3"
                >
                  <div>
                    <p className="font-semibold text-slate-900">{reminder.title}</p>
                    <p className="text-xs text-slate-500">{reminder.channel}</p>
                  </div>
                  <span className="text-sm font-semibold text-brand-blue">{reminder.due}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="glass-panel space-y-3 p-6">
          <div>
            <p className="section-title mb-1">Resource readiness</p>
            <h3 className="text-lg font-semibold text-slate-900">Inventory watch</h3>
          </div>
          {isLoading ? (
            <p className="rounded-2xl border border-dashed border-slate-200 p-4 text-sm text-slate-400">
              Syncing supply signals...
            </p>
          ) : (
            <ul className="space-y-3 text-sm">
              {inventoryAlerts.map((alert) => (
                <li key={alert.item} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <p className="font-semibold text-slate-900">{alert.item}</p>
                  <p className="text-xs text-slate-500">{alert.status}</p>
                  <p className="text-xs text-brand-blue">{alert.action}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="glass-panel p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
          <div>
            <p className="section-title mb-1">Surgery prep</p>
            <h3 className="text-lg font-semibold text-slate-900">Operating room status</h3>
          </div>
          <button className="rounded-xl bg-slate-900 px-4 py-2 text-xs font-semibold text-white">
            Publish to teams
          </button>
        </div>
        {isLoading ? (
          <p className="rounded-2xl border border-dashed border-slate-200 p-4 text-sm text-slate-400">
            Loading surgery board...
          </p>
        ) : (
          <div className="mt-4 grid gap-4 md:grid-cols-3">
            {surgeryBoard.map((entry) => (
              <div key={entry.case} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                  {entry.time}
                </p>
                <p className="mt-2 text-sm font-semibold text-slate-900">{entry.case}</p>
                <p className="text-xs text-slate-500">Room: {entry.room}</p>
                <p className="mt-2 text-xs font-semibold text-brand-blue">{entry.lead}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
};

export default CarePlans;
